/* RecommendationStrip.jsx, inline placement that triggers the MiniMatcher
   popup. Use this anywhere on the platform where a contextual nudge
   makes sense (marketing articles, council guides, build cost reports,
   workspace tool sidebars).

   It renders a quietly polished call-to-action with the business
   preview (logo + name + one-liner) when a partner is involved, or a
   generic "find a..." CTA when the engine is involved.

   Phase 1 (2026-05-29).
*/
import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchRecommendations,
  trackImpression,
  getOrCreateSessionId,
} from '../../services/recommendationsService';
import MiniMatcher from './MiniMatcher';
import { useAuth } from '../../workspace/context/AuthContext';
import { markFinanceSorted } from './useSmartRec';

export default function RecommendationStrip({
  category,
  sourcePlacement,
  sourcePage,
  state,
  eyebrow,
  headline,
  body,
  ctaLabel = 'Find help',
  tone = 'light', // 'light' | 'dark'
  linkTo = null,  // if set, CTA navigates here instead of opening MiniMatcher
  allowFinanceSorted = false,
  onFinanceSorted = null,
}) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();
  // Detect logged-in state, when present, anchor the popup to the
  // LEFT so it doesn't collide with the workspace Quick-Add `+` FAB.
  let isLoggedIn = false;
  try {
    const auth = useAuth();
    isLoggedIn = !!auth?.user;
  } catch (e) { /* logged-out branch */ }

  // Pre-fetch the recommendation so we can show a logo/preview chip in
  // the strip itself. Cheap call, same one the popup will reuse.
  useEffect(() => {
    let cancelled = false;
    fetchRecommendations({
      category,
      state,
      sourcePlacement,
      sessionId: getOrCreateSessionId(),
      limit: 1,
    }).then((data) => {
      if (cancelled) return;
      const first = (data.results || [])[0] || null;
      setPreview(first);
      if (first) {
        trackImpression({
          category,
          sourcePlacement,
          businessId: first.id,
          partnerKey: first.partner_key,
          sessionId: getOrCreateSessionId(),
          state: state || null,
          shownPosition: 1,
          clicked: false,
        });
      }
    });
    return () => { cancelled = true; };
  }, [category, sourcePlacement, state]);

  return (
    <section
      className={`bp-rec-strip bp-no-pad ${tone === 'dark' ? 'is-dark' : ''}${hidden ? ' is-hidden' : ''}`}
      data-testid={`rec-strip-${sourcePlacement}`}
      hidden={hidden}
    >
      <div className="bp-rec-strip-inner">
        <div className="bp-rec-strip-text">
          <h3 className="bp-rec-strip-headline">{headline}</h3>
          {body && <p className="bp-rec-strip-body">{body}</p>}
          {allowFinanceSorted && (
            <button
              type="button"
              className="bp-rec-strip-alt"
              onClick={() => {
                markFinanceSorted();
                setHidden(true);
                if (onFinanceSorted) onFinanceSorted();
              }}
              data-testid={`rec-strip-finance-sorted-${sourcePlacement}`}
            >
              I already have finance sorted →
            </button>
          )}
        </div>
        <div className="bp-rec-strip-cta">
          <button
            type="button"
            className="bp-rec-strip-btn"
            onClick={() => {
              trackImpression({
                category,
                sourcePlacement,
                businessId: preview?.id,
                partnerKey: preview?.partner_key,
                sessionId: getOrCreateSessionId(),
                state: state || null,
                clicked: true,
              });
              // When linkTo is supplied (e.g. professional:builder routes
              // to the full /workspace/tools/find-a-builder tool) we
              // navigate instead of opening the small popup. The full
              // 11-question wizard always wins for builder matches.
              // We pass the intent via `state.from` so the workspace
              // protected-route → login page preserves the destination
              // and the user lands DIRECTLY on the wizard after sign-in.
              if (linkTo) {
                navigate(linkTo, { state: { from: linkTo } });
              } else {
                setOpen(true);
              }
            }}
            data-testid={`rec-strip-cta-${sourcePlacement}`}
          >
            {ctaLabel} <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <MiniMatcher
        open={open}
        onClose={() => setOpen(false)}
        category={category}
        sourcePlacement={sourcePlacement}
        sourcePage={sourcePage}
        initialState={state}
        heading={ctaLabel}
        subheading={preview?.tagline || null}
        anchorSide={isLoggedIn ? 'left' : 'right'}
      />
    </section>
  );
}
